import { CalendarWeek, LockFill } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';

import { Avatar, Tag, Typography } from 'antd';

import { getCdnUrl } from '@/helpers';
import { formatDate } from '@/helpers/formatDate';
import { useLog } from '@/hooks/useLog';
import { enStatus, IMyCourse } from '@/interfaces/course';
import styled, { StyledProp } from '@/styled';

interface Props extends StyledProp {
  course: Partial<IMyCourse>;
  showStatus?: boolean;
}

const ShortInfo = (props: Props) => {
  const navigate = useNavigate();
  const { activity } = useLog();

  const goToCourse = () => {
    navigate(`/curso/${props?.course?.hash}`);
    activity('clickCourse', {
      message: `Clicou no curso: ${props.course.title}`,
      courseHash: props.course.hash
    });
  };

  const nameMapper: Record<string, any> = {
    [enStatus.EXPIRED]: 'Expirado',
    [enStatus.BLOCKED]: 'Bloqueado',
    [enStatus.NOT_RELEASED]: 'Agendado'
  };

  const iconMapper: Record<string, any> = {
    [enStatus.EXPIRED]: <LockFill size={12} />,
    [enStatus.BLOCKED]: <LockFill size={12} />,
    [enStatus.NOT_RELEASED]: <CalendarWeek size={12} />
  };

  const showTagStatus = [enStatus.EXPIRED, enStatus.BLOCKED, enStatus.NOT_RELEASED].includes(props?.course?.status);
  const messageExpireAt = props?.course?.expire_at ? `Expira em ${formatDate(props?.course?.expire_at)}` : '';

  return (
    <div className={props.className}>
      <Typography.Title className='mycourse-title' level={4} ellipsis onClick={goToCourse}>
        {props.course.title}
      </Typography.Title>

      <div className='space'>
        <div className='mycourse-modules'>
          <Typography.Text ellipsis>
            {props.course.total_modules} Módulos | {props?.course?.total_lessons} Aulas
          </Typography.Text>
        </div>

        {props.showStatus && (
          <Tag data-show={showTagStatus}>
            {iconMapper[props.course.status]} <Typography.Text>{nameMapper[props.course.status]}</Typography.Text>
          </Tag>
        )}
      </div>

      {props?.course?.description && (
        <Typography.Paragraph className='mycourse-description' ellipsis={{ rows: 4 }}>
          {props?.course?.description}
        </Typography.Paragraph>
      )}

      <div className='mycourse-expires'>
        <Typography.Text ellipsis>{messageExpireAt}</Typography.Text>
      </div>

      <div className='avatar'>
        <Avatar src={getCdnUrl(props?.course?.author?.avatar)}>
          {(props.course?.author?.name ?? '').split('')[0]}
        </Avatar>
        <Typography.Text ellipsis>{props.course.author?.name}</Typography.Text>
      </div>
    </div>
  );
};

export default styled(ShortInfo)`
  padding-bottom: ${({ theme }) => theme.space.pxToRem(2)};

  .space {
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    gap: ${({ theme }) => theme.space.pxToRem(8)};

    .ant-typography {
      margin: 0;
    }
  }

  .ant-tag {
    display: none;
    align-items: center;
    justify-content: center;
    width: fit-content;
    gap: ${({ theme }) => theme.space.pxToRem(6)};
    padding: 1px 8px;

    .ant-typography {
      font-size: ${({ theme }) => theme.space.pxToRem(12)} !important;
    }

    &[data-show='true'] {
      display: flex;
    }
  }

  .mycourse-title {
    margin: 0;
    margin-bottom: 4px;
    line-height: 1;
  }

  .mycourse-modules {
    span.ant-typography {
      font-size: ${({ theme }) => theme.space.pxToRem(14)};
      color: ${({ theme }) => theme.colorTextTertiary};
    }
  }

  .mycourse-description {
    font-size: ${({ theme }) => theme.space.pxToRem(14)};
  }

  .mycourse-expires {
    span.ant-typography {
      font-size: ${({ theme }) => theme.space.pxToRem(12)};
      color: ${({ theme }) => theme.colorTextTertiary};
    }
  }

  .avatar {
    margin-top: ${({ theme }) => theme.space.spacing(2)};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.space.spacing(2)};

    span.ant-typography {
      font-size: ${({ theme }) => theme.space.pxToRem(14)};
    }

    .ant-avatar {
      min-width: 24px;
      min-height: 24px;
      max-width: 24px;
      max-height: 24px;

      .ant-avatar-string {
        line-height: 24px;
      }
    }
  }
`;
